import { i18next } from "dash-ui";
import { Component, ErrorInfo, ReactNode } from "react";

type Props = { children: ReactNode };
type State = { error: Error | null };

const RELOAD_STAMP_KEY = "chunk-reload-at";
const RELOAD_COOLDOWN_MS = 15_000;

/**
 * Routes are code-split, so a deploy replaces the chunk files an already-open
 * tab still points at. Navigating then throws and React unmounts the whole tree
 * — a blank page. Reload once to pick up the new build.
 *
 * The cooldown stamp is what stops a reload loop: if we already reloaded within
 * the last few seconds and the chunk still fails, the problem is not staleness,
 * so we show a message instead of reloading again.
 */
function isChunkLoadError(error: Error): boolean {
  return (
    error.name === "ChunkLoadError" ||
    /Loading chunk .* failed|Loading CSS chunk .* failed|Failed to fetch dynamically imported module/i.test(
      error.message
    )
  );
}

function reloadedRecently(): boolean {
  try {
    const at = Number(sessionStorage.getItem(RELOAD_STAMP_KEY));
    return Boolean(at) && Date.now() - at < RELOAD_COOLDOWN_MS;
  } catch {
    // storage blocked: assume we already tried, so we never loop
    return true;
  }
}

function stampReload(): void {
  try {
    sessionStorage.setItem(RELOAD_STAMP_KEY, String(Date.now()));
  } catch {
    /* storage unavailable */
  }
}

export class ChunkErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    if (isChunkLoadError(error) && !reloadedRecently()) {
      stampReload();
      window.location.reload();
    }
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    if (isChunkLoadError(error)) {
      console.error("Chunk failed to load", error, info.componentStack);
    }
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    // Not ours — let it reach the global handler and Sentry.
    if (!isChunkLoadError(error)) throw error;

    return (
      <div className="flex h-screen w-full flex-col items-center justify-center gap-4 p-4 text-center">
        <p className="text-lg text-n90">
          {i18next.t("pages:chunkErrorBoundary.newVersion")}
        </p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="rounded bg-b50 px-4 py-2 text-md text-white hover:bg-b50Hover"
        >
          {i18next.t("pages:chunkErrorBoundary.reload")}
        </button>
      </div>
    );
  }
}
